import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Building2, Users, Filter, ArrowUpDown, ChartLine as LineChart, ChevronRight, Briefcase, Globe, Lock } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useApi } from '../../lib/useApi';
import { resolveLogoUrl } from '../../lib/logo';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import type { Account } from '../../types';
import type { EAWorkspace, EAStatus, ReadinessLevel } from '../../types/executiveAnalytics';

type SortField = 'name' | 'status' | 'readiness' | 'updated';
type StatusFilter = 'all' | 'not_started' | 'configure_in_progress' | 'analytics_ready' | 'report_ready';

const STATUS_LABELS: Record<EAStatus, string> = {
  not_started: 'Not Started',
  configure_in_progress: 'Configuring',
  analytics_ready: 'Analytics Ready',
  report_ready: 'Report Ready',
};

const STATUS_COLORS: Record<EAStatus, string> = {
  not_started: 'bg-app-canvas text-app-muted',
  configure_in_progress: 'bg-app-warning/10 text-app-warning',
  analytics_ready: 'bg-app-focus/10 text-app-focus',
  report_ready: 'bg-app-positive/10 text-app-positive',
};

const READINESS_COLORS: Record<ReadinessLevel, string> = {
  low: 'bg-app-critical/10 text-app-critical',
  medium: 'bg-app-warning/10 text-app-warning',
  high: 'bg-app-positive/10 text-app-positive',
};

interface AccountWithWorkspace extends Account {
  workspace?: EAWorkspace | null;
  intelligence_completeness: number;
}

export default function ExecutiveAnalytics() {
  const { organisation, user } = useAuth();
  const { apiGet } = useApi();
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState<AccountWithWorkspace[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortAsc, setSortAsc] = useState(true);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [verticalFilter, setVerticalFilter] = useState('');
  const [publicFilter, setPublicFilter] = useState<'all' | 'public' | 'private'>('all');
  const [showFilters, setShowFilters] = useState(false);

  const loadData = useCallback(async () => {
    if (!organisation || !user) return;

    try {
      const data = await apiGet<{ accounts: AccountWithWorkspace[] }>('/v1/ea/accounts');
      setAccounts(data.accounts || []);
    } catch {
      setAccounts([]);
    }
    setLoading(false);
  }, [organisation, user, apiGet]);

  useEffect(() => { loadData(); }, [loadData]);

  const verticals = [...new Set(accounts.map(a => a.industry).filter(Boolean))].sort();

  const filtered = accounts
    .filter(a => a.company_name.toLowerCase().includes(searchQuery.toLowerCase()))
    .filter(a => {
      if (statusFilter === 'all') return true;
      const ws = a.workspace;
      if (statusFilter === 'not_started') return !ws;
      return ws?.status === statusFilter;
    })
    .filter(a => !verticalFilter || a.industry === verticalFilter)
    .filter(a => {
      if (publicFilter === 'all') return true;
      if (publicFilter === 'public') return a.is_public_company === true;
      return !a.is_public_company;
    });

  const sorted = [...filtered].sort((a, b) => {
    const dir = sortAsc ? 1 : -1;
    switch (sortField) {
      case 'name': return a.company_name.localeCompare(b.company_name) * dir;
      case 'status': {
        const sa = a.workspace?.status || 'not_started';
        const sb = b.workspace?.status || 'not_started';
        return sa.localeCompare(sb) * dir;
      }
      case 'readiness': {
        const ra = a.workspace?.deal_quality_score || 0;
        const rb = b.workspace?.deal_quality_score || 0;
        return (ra - rb) * dir;
      }
      case 'updated': return (new Date(a.updated_at).getTime() - new Date(b.updated_at).getTime()) * dir;
      default: return 0;
    }
  });

  const handleSort = (field: SortField) => {
    if (sortField === field) setSortAsc(!sortAsc);
    else { setSortField(field); setSortAsc(true); }
  };

  if (loading) return <div className="flex items-center justify-center h-screen"><LoadingSpinner size="lg" /></div>;

  return (
    <div className="app-page">
      <div className="mb-2 flex items-center justify-between border-b border-app-line pb-6">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center bg-app-accent text-app-accent-text">
            <LineChart className="w-5 h-5" />
          </div>
          <div>
            <p className="app-kicker mb-1">Commercial evidence</p>
            <h1 className="font-display text-3xl font-semibold text-app-ink sm:text-4xl">Executive Analytics</h1>
            <p className="text-sm text-app-muted mt-0.5">
              Financial analysis and executive decision support
            </p>
          </div>
        </div>
      </div>

      <div className="mb-6 mt-6 flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-app-muted" />
          <input
            type="text"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder="Search accounts..."
            className="input-field !pl-10 !py-2.5"
          />
        </div>
        <button
          onClick={() => setShowFilters(!showFilters)}
          className={`btn-ghost !py-2.5 !px-3 ${showFilters ? 'bg-app-canvas' : ''}`}
        >
          <Filter className="w-4 h-4 mr-1.5" /> Filters
        </button>
        <button onClick={() => handleSort('name')} className="btn-ghost !py-2.5 !px-3">
          <ArrowUpDown className="w-4 h-4 mr-1.5" /> Sort
        </button>
      </div>

      {showFilters && (
        <div className="mb-6 p-4 bg-app-surface rounded-lg border border-app-line flex items-center gap-4 flex-wrap animate-fade-in">
          <div>
            <label className="text-xs font-medium text-app-muted mb-1 block">Status</label>
            <select
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value as StatusFilter)}
              className="input-field !py-2 !text-sm !w-auto"
            >
              <option value="all">All Statuses</option>
              <option value="not_started">Not Started</option>
              <option value="configure_in_progress">Configuring</option>
              <option value="analytics_ready">Analytics Ready</option>
              <option value="report_ready">Report Ready</option>
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-app-muted mb-1 block">Vertical</label>
            <select
              value={verticalFilter}
              onChange={e => setVerticalFilter(e.target.value)}
              className="input-field !py-2 !text-sm !w-auto"
            >
              <option value="">All Verticals</option>
              {verticals.map(v => <option key={v} value={v}>{v}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-app-muted mb-1 block">Company Type</label>
            <select
              value={publicFilter}
              onChange={e => setPublicFilter(e.target.value as 'all' | 'public' | 'private')}
              className="input-field !py-2 !text-sm !w-auto"
            >
              <option value="all">All</option>
              <option value="public">Public</option>
              <option value="private">Private</option>
            </select>
          </div>
        </div>
      )}

      {sorted.length === 0 ? (
        <div className="text-center py-20">
          <Building2 className="w-12 h-12 text-app-line-strong mx-auto mb-4" />
          <h3 className="text-lg font-medium text-app-ink mb-1">No processed accounts</h3>
          <p className="text-sm text-app-muted max-w-md mx-auto">
            Accounts must be enriched in the Accounts section before they appear here for executive analytics.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {sorted.map(a => (
            <EAAccountCard
              key={a.id}
              account={a}
              onClick={() => navigate(`/app/executive-analytics/${a.id}`)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function EAAccountCard({ account, onClick }: { account: AccountWithWorkspace; onClick: () => void }) {
  const ws = account.workspace;
  const status: EAStatus = ws?.status || 'not_started';
  const readiness: ReadinessLevel = ws?.readiness_level || 'low';
  const confidence = ws?.confidence_score || 0;

  const [logoFailed, setLogoFailed] = useState(false);
  const logoSrc = resolveLogoUrl(account.logo_url, account.website_url);

  const actionLabel = !ws ? 'Start Analysis' : status === 'report_ready' ? 'View Report' : 'Continue';

  return (
    <div
      onClick={onClick}
      className="group relative bg-app-surface rounded-lg border border-app-line shadow-none hover:shadow-none hover:border-app-brand/20 transition duration-300 cursor-pointer overflow-hidden"
    >
      <div className="h-1 bg-app-accent" />

      <div className="p-5">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3 min-w-0">
            {logoSrc && !logoFailed ? (
              <img
                src={logoSrc}
                alt=""
                className="w-10 h-10 rounded-lg object-cover bg-app-canvas flex-shrink-0"
                onError={() => setLogoFailed(true)}
              />
            ) : (
              <div className="w-10 h-10 bg-app-brand/10 rounded-lg flex items-center justify-center flex-shrink-0">
                <span className="text-lg font-bold text-app-brand">{account.company_name.charAt(0)}</span>
              </div>
            )}
            <div className="min-w-0">
              <h3 className="text-sm font-semibold text-app-ink truncate">{account.company_name}</h3>
              {account.industry && (
                <p className="text-xs text-app-muted truncate">{account.industry}</p>
              )}
            </div>
          </div>

          <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium flex-shrink-0 ${STATUS_COLORS[status]}`}>
            {STATUS_LABELS[status]}
          </span>
        </div>

        <div className="flex items-center gap-3 mb-3 text-xs text-app-muted">
          {account.employee_count > 0 && (
            <span className="flex items-center gap-1">
              <Users className="w-3 h-3" /> {account.employee_count.toLocaleString()}
            </span>
          )}
          <span className="flex items-center gap-1">
            {account.is_public_company ? (
              <><Globe className="w-3 h-3" /> Public</>
            ) : (
              <><Lock className="w-3 h-3" /> Private</>
            )}
          </span>
          {account.ticker_symbol && (
            <span className="font-mono text-app-muted">{account.ticker_symbol}</span>
          )}
        </div>

        <div className="flex items-center gap-2 mb-3">
          <div className="flex-1">
            <div className="flex items-center justify-between text-[10px] text-app-muted mb-1">
              <span>Intelligence</span>
              <span>{account.intelligence_completeness}%</span>
            </div>
            <div className="h-1.5 bg-app-line rounded-full overflow-hidden" role="progressbar" aria-label={`${account.company_name} intelligence completeness`} aria-valuemin={0} aria-valuemax={100} aria-valuenow={account.intelligence_completeness}>
              <div
                className="h-full bg-app-brand rounded-full transition duration-500"
                style={{ width: `${account.intelligence_completeness}%` }}
              />
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {ws && ws.status !== 'not_started' && (
              <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium ${READINESS_COLORS[readiness]}`}>
                {readiness.charAt(0).toUpperCase() + readiness.slice(1)} Readiness
              </span>
            )}
            {confidence > 0 && (
              <span className="text-[10px] text-app-muted font-medium">
                {Math.round(confidence)}% confidence
              </span>
            )}
          </div>

          <button className="flex items-center gap-1 text-xs font-medium text-app-brand group-hover:text-app-brand transition-colors">
            {actionLabel}
            <ChevronRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" />
          </button>
        </div>
      </div>
    </div>
  );
}
